/**
 * 処理の状況（`provisioning_jobs`）の一覧。
 *
 * ドメイン追加・メールボックス作成・プラン変更などは非同期で、画面は
 * `202 Accepted` の `jobId` を受け取ってから進捗を見る形になっている。
 * ところが**一覧が無かった**ため、`<JobProgress>` を出していた画面を離れると
 * 処理の行方を追う手段が消えていた（「追加したはずのドメインが出てこない」の
 * 原因がどこにあるのかを契約者が確かめられない）。
 *
 * 失敗の理由は `userMessage`（画面に出してよい日本語）だけを見せる。
 * 内部のエラー本文は API が返さない。
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  CursorPagination,
  DataTable,
  PageHeader,
  Section,
  StatusBadge,
  formatDateTime,
  useCursorPagination,
  type DataTableColumn,
} from '@mail-sv/ui';
import { api, errorMessage } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { HelpNote } from '../components/help.js';
import type { CursorPage, JobProgress } from '../lib/api-types.js';

/** ジョブ種別の日本語。`provisioning_jobs.kind` の語彙に対応する。 */
const KIND_LABEL: Record<string, string> = {
  domain_provision: 'ドメインの追加',
  domain_deprovision: 'ドメインの削除',
  mailbox_provision: 'メールアドレスの作成',
  mailbox_deprovision: 'メールアドレスの削除',
  mailbox_update: 'メールアドレスの変更',
  alias_provision: '転送・エイリアスの作成',
  plan_change: 'プランの変更',
  dns_verify: 'DNS の確認',
  certificate_issue: '証明書の取得',
  payment_method_setup: 'お支払い方法の登録',
  organization_migrate: 'サーバーの移設',
};

const STATE_LABEL: Record<string, string> = {
  queued: '順番待ち',
  running: '実行中',
  succeeded: '完了',
  failed: '失敗',
  canceled: '取り消し',
};

export function JobsPage() {
  const page = useCursorPagination(20);

  const list = useQuery({
    queryKey: qk.jobs({ cursor: page.cursor, limit: page.limit }),
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(page.limit) });
      if (page.cursor) params.set('cursor', page.cursor);
      return api.get<CursorPage<JobProgress>>(`/jobs?${params.toString()}`);
    },
    // 動いているものがあるうちだけ追いかける。
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some((j) => j.state === 'queued' || j.state === 'running')
        ? 4000
        : false,
  });

  const columns: Array<DataTableColumn<JobProgress>> = [
    {
      id: 'kind',
      header: '処理',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{KIND_LABEL[row.kind] ?? row.kind}</p>
          <p className="truncate text-xs text-muted-foreground">{formatDateTime(row.createdAt)}</p>
        </div>
      ),
    },
    {
      id: 'state',
      header: '状態',
      width: 'w-32',
      cell: (row) => (
        <StatusBadge
          status={row.state}
          label={STATE_LABEL[row.state] ?? row.state}
          size="sm"
          dot
          pulse={row.state === 'running'}
        />
      ),
    },
    {
      id: 'progress',
      header: '進み',
      width: 'w-28',
      hideBelow: 'md',
      align: 'right',
      cell: (row) =>
        row.state === 'queued' || row.state === 'running' ? (
          <span className="tabular text-xs">{row.progress}%</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      id: 'message',
      header: '内容',
      hideBelow: 'lg',
      cell: (row) =>
        row.userMessage ? (
          <span className="text-xs text-muted-foreground">{row.userMessage}</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      id: 'finishedAt',
      header: '完了',
      width: 'w-40',
      hideBelow: 'lg',
      cell: (row) => (
        <span className="tabular text-xs">
          {row.finishedAt ? formatDateTime(row.finishedAt) : '—'}
        </span>
      ),
    },
  ];

  const failed = (list.data?.items ?? []).filter((j) => j.state === 'failed');

  return (
    <>
      <PageHeader
        title="処理の状況"
        description="ドメインの追加やメールアドレスの作成など、時間のかかる処理の進み具合です。"
      />

      <div className="space-y-5">
        {failed.length > 0 ? (
          <HelpNote title="失敗した処理があります">
            {failed.length} 件の処理が失敗しています。多くは DNS の設定待ちなど、
            条件が整えばやり直しで解決します。同じ操作をもう一度お試しいただき、
            それでも失敗する場合はサポートへお問い合わせください。
          </HelpNote>
        ) : null}

        <Section title="最近の処理">
          <DataTable
            columns={columns}
            rows={list.data?.items ?? []}
            rowKey={(row) => row.jobId}
            loading={list.isPending}
            error={list.isError ? errorMessage(list.error) : undefined}
            emptyTitle="処理の記録はありません"
            emptyDescription="ドメインやメールアドレスを追加すると、ここに表示されます。"
            footer={
              <CursorPagination
                nextCursor={list.data?.nextCursor ?? null}
                canGoPrev={page.canGoPrev}
                onNext={() => page.goNext(list.data?.nextCursor ?? null)}
                onPrev={page.goPrev}
                limit={page.limit}
                onLimitChange={page.setLimit}
                currentCount={list.data?.items.length}
                loading={list.isFetching}
              />
            }
          />
          <p className="text-xs text-muted-foreground">
            <Badge variant="muted" size="sm">
              補足
            </Badge>{' '}
            「順番待ち」「実行中」のあいだは自動で更新されます。画面を開き直す必要はありません。
          </p>
        </Section>
      </div>
    </>
  );
}
